import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Divider,
    FlexCol,
    FlexContainer,
    FlexRow,
    Checkbox,
    Notification,
    TextField,
    Select,
    SelectOption,
    SpinnerModal
} from 'sarsaparilla';
import { map, cloneDeep, set } from 'lodash';

import { AccountProps } from '../../props/account';
import { ApiKeyProps } from '../../props/apiKey';
import { OrgOptionProps } from '../../props/organization';

import * as styles from '../../containers/UserAdminPage/UserAdminPage.scss';

const propTypes = {
    account: PropTypes.shape({
        account: AccountProps,
        apiKey: ApiKeyProps
    }),
    orgOptions: PropTypes.arrayOf(OrgOptionProps),
    onEnable: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
};

const defaultProps = {
    isDisabled: true,
    isLoading: false
};

const UserDisplay = ({
    isDisabled,
    isLoading,
    account,
    orgOptions,
    onEnable,
    onChange
}) => {
    const {
        account: userAccount,
        apiKey
    } = account;

    const hasResult = !!userAccount.accountId;
    const isDeveloper = apiKey.externalId && apiKey.apiKey !== '';

    const [pendingApiKey, updateApiKey] = useState(apiKey);
    const [isUpdateAccountSuccess, updateAccountSuccess] = useState(false);
    const [isEnableDeveloperAccessSuccessful, updateDeveloperAccessSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        updateApiKey(account.apiKey)
    }, [account]);

    const enableDeveloperAccess = () => {
        onEnable()
            .then(() => {
                updateDeveloperAccessSuccess(true);
                updateAccountSuccess(false);
                setError(null);
            })
            .catch((responseError) => {
                setError(responseError);
                updateDeveloperAccessSuccess(false);
                updateAccountSuccess(false);
            });
    }

    const updateInput = (key, value) => {
        const newPendingApiKey = cloneDeep(pendingApiKey);
        set(newPendingApiKey, key, value);
        updateApiKey(newPendingApiKey);
    }

    const handleSubmit = () => {
        onChange(pendingApiKey)
            .then(() => {
                updateAccountSuccess(true);
                updateDeveloperAccessSuccess(false);
                setError(null);
            })
            .catch((responseError) => {
                setError(responseError);
                updateAccountSuccess(false);
                updateDeveloperAccessSuccess(false);
            })
    }

    return (
        <FlexContainer>
            <SpinnerModal
                isOpen={isLoading}
                isSpinning={isLoading} />

            { isEnableDeveloperAccessSuccessful && (
                <Notification
                    type="success"
                    className="mb-2"
                    dismissNotification={() => updateDeveloperAccessSuccess(false)}
                    shouldFocusOnMount>
                    Enable Developer Access Successful
                </Notification>
            )}

            { isUpdateAccountSuccess && (
                <Notification
                    type="success"
                    className="mb-2"
                    dismissNotification={() => updateAccountSuccess(false)}
                    shouldFocusOnMount>
                    Update Account Successful
                </Notification>
            )}

            { error && (
                <Notification
                    type="error"
                    className="mb-2"
                    shouldFocusOnMount>
                    {error}
                </Notification>
            )}

            <FlexRow>
                <FlexCol>
                    { !hasResult && (
                        <p>No results</p>
                    )}

                    { hasResult && isDeveloper && (
                        <div>
                            <FlexRow className="mb-2">
                                <FlexCol>
                                    <TextField
                                        id="user-first-name"
                                        label="First Name"
                                        value={userAccount.firstName}
                                        isDisabled />
                                </FlexCol>
                                <FlexCol>
                                    <TextField
                                        id="user-last-name"
                                        label="Last Name"
                                        value={userAccount.lastName}
                                        isDisabled />
                                </FlexCol>
                            </FlexRow>

                            <FlexRow className="mb-2">
                                <FlexCol>
                                    <TextField
                                        id="user-email"
                                        label="Email"
                                        value={userAccount.email}
                                        isDisabled />
                                </FlexCol>
                                <FlexCol>
                                    <TextField
                                        id="user-api-key"
                                        label="API key"
                                        value={account.apiKey.apiKey}
                                        isDisabled />
                                </FlexCol>
                            </FlexRow>

                            <Divider />

                            <Checkbox
                                id="user-locked"
                                className={`${styles.danger} mb-1`}
                                label="Disable API Access"
                                value="controlled"
                                isChecked={!!pendingApiKey.locked}
                                isDisabled={isDisabled}
                                onChange={(e) => updateInput('locked', e.target.checked)} />

                            <Checkbox
                                id="user-data-steward-access"
                                className="mb-1"
                                label="Data Steward Access"
                                value="controlled"
                                isChecked={!!pendingApiKey.writeAccess}
                                isDisabled={isDisabled}
                                onChange={(e) => updateInput('writeAccess', e.target.checked)} />

                            <Select
                                id="user-search-org"
                                className="mb-1"
                                label="Organization"
                                value={pendingApiKey.orgId}
                                isDisabled={isDisabled || !pendingApiKey.writeAccess}
                                onChange={(e) => updateInput('orgId', e.target.value)}>

                                <SelectOption value="" isDisabled>All Organizations</SelectOption>

                                { map(orgOptions, ({ label, value }) => {
                                    return <SelectOption key={value} value={value}>{label}</SelectOption>
                                })}
                            </Select>

                            <Checkbox
                                id="user-admin-access"
                                label="Admin Access"
                                value="controlled"
                                isChecked={!!pendingApiKey.adminAccess}
                                isDisabled={isDisabled}
                                onChange={(e) => updateInput('adminAccess', e.target.checked)} />

                            <Divider />

                            <Button
                                onClick={handleSubmit}>
                                Save Changes
                            </Button>
                        </div>
                    )}
                    
                    { hasResult && !isDeveloper && (
                        <div>
                            <Notification
                                type="info"
                                title='Developer Access not enabled'>
                                User has not enabled developer access for this account
                            </Notification>

                            <Button
                                appearance="secondary"
                                className="enable-developer-access-btn"
                                onClick={enableDeveloperAccess}>
                                Enable Developer Access For This User
                            </Button>
                        </div>
                    )}
                </FlexCol>
            </FlexRow>
        </FlexContainer>
    );
}

UserDisplay.propTypes = propTypes;
UserDisplay.defaultProps = defaultProps;

export default UserDisplay;